import { useCallback, useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useDropzone } from 'react-dropzone'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { createLocation, uploadFile } from '../../lib/api'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'

const schema = z.object({
  country:      z.string().min(2).max(100),
  city:         z.string().min(2).max(100),
  countryLabel: z.string().min(2).max(200),
  cityLabel:    z.string().min(2).max(200),
  latitude:     z.number().nullable().optional(),
  longitude:    z.number().nullable().optional(),
})

function slugify(str) {
  return (str ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().trim()
    .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-')
}

// Nominatim autocomplete hook — debounced
function useNominatim(query) {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const timer = useRef(null)

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return }
    clearTimeout(timer.current)
    timer.current = setTimeout(async () => {
      setLoading(true)
      try {
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=6&addressdetails=1&featuretype=city`
        const res = await fetch(url, { headers: { 'Accept-Language': 'en' } })
        const data = await res.json()
        // Keep only results that have a city / town / village
        const filtered = data.filter(r =>
          r.address?.city || r.address?.town || r.address?.village || r.address?.municipality
        )
        setResults(filtered)
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 400)
    return () => clearTimeout(timer.current)
  }, [query])

  return { results, loading }
}

export default function LocationForm() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [pendingFlag, setPendingFlag] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [selected, setSelected] = useState(null) // filled Nominatim result
  const dropdownRef = useRef(null)

  const { results, loading } = useNominatim(searchQuery)

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { country: '', city: '', countryLabel: '', cityLabel: '', latitude: null, longitude: null },
  })

  // Close dropdown on outside click
  useEffect(() => {
    function handler(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  function pickResult(r) {
    const addr = r.address
    const cityLabel    = addr.city || addr.town || addr.village || addr.municipality || ''
    const countryLabel = addr.country || ''
    const country      = slugify(addr.country_code?.toUpperCase() || addr.country || '')
    const city         = slugify(cityLabel)
    const lat          = parseFloat(r.lat)
    const lng          = parseFloat(r.lon)

    setValue('country',      country,      { shouldValidate: true })
    setValue('city',         city,         { shouldValidate: true })
    setValue('countryLabel', countryLabel, { shouldValidate: true })
    setValue('cityLabel',    cityLabel,    { shouldValidate: true })
    setValue('latitude',     lat)
    setValue('longitude',    lng)
    setSelected({ cityLabel, countryLabel, lat, lng })
    setSearchQuery(cityLabel)
    setShowDropdown(false)
  }

  const onDrop = useCallback((files) => {
    if (!files[0]) return
    setPendingFlag(prev => {
      if (prev) URL.revokeObjectURL(prev.preview)
      return { file: files[0], preview: URL.createObjectURL(files[0]) }
    })
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp'] },
    maxFiles: 1,
  })

  function clearFlag(e) {
    e?.stopPropagation()
    if (pendingFlag) URL.revokeObjectURL(pendingFlag.preview)
    setPendingFlag(null)
  }

  const createMut = useMutation({
    mutationFn: async (data) => {
      let flagUrl = ''
      if (pendingFlag) {
        const result = await uploadFile(pendingFlag.file, 'Location', null)
        flagUrl = result.url || result.publicUrl || result.relativePath || ''
      }
      return createLocation({ ...data, flag: flagUrl })
    },
    onSuccess: (result) => {
      qc.setQueryData(['locations'], old => [...(old ?? []), result])
      toast.success('Location created')
      navigate('/locations')
    },
    onError: (e) => toast.error(e.message),
  })

  const watchedCity = watch('cityLabel')
  const watchedCountry = watch('countryLabel')
  const watchedLat = watch('latitude')
  const watchedLng = watch('longitude')

  return (
    <div>
      <div className="flex items-center gap-3 mb-8">
        <Link to="/locations"><Button variant="ghost" size="sm"><i className="ti ti-arrow-left" /></Button></Link>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Add Location</h1>
          <p className="text-sm text-gray-500 mt-0.5">New city or country</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(d => createMut.mutate(d))} className="space-y-6">

        {/* Nominatim Search */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Search City</h2>

          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              City name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setShowDropdown(true); setSelected(null) }}
                onFocus={() => results.length > 0 && setShowDropdown(true)}
                placeholder="Type a city name… e.g. Berlin, Vienna, Toronto"
                className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3.5 pr-10 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                {loading
                  ? <i className="ti ti-loader-2 animate-spin text-gray-400" />
                  : <i className="ti ti-search text-gray-400" />
                }
              </div>
            </div>

            {/* Dropdown */}
            {showDropdown && results.length > 0 && (
              <ul className="absolute z-50 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
                {results.map((r, i) => {
                  const addr = r.address
                  const city    = addr.city || addr.town || addr.village || addr.municipality || r.display_name
                  const country = addr.country || ''
                  const state   = addr.state || ''
                  return (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => pickResult(r)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-indigo-50 transition-colors border-b border-gray-100 last:border-0"
                      >
                        <i className="ti ti-map-pin text-indigo-400 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{city}{state ? `, ${state}` : ''}</p>
                          <p className="text-xs text-gray-500 truncate">{country}</p>
                        </div>
                        <span className="ml-auto text-xs text-gray-400 shrink-0">
                          {parseFloat(r.lat).toFixed(4)}, {parseFloat(r.lon).toFixed(4)}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}

            {showDropdown && !loading && searchQuery.length >= 2 && results.length === 0 && (
              <div className="absolute z-50 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg px-4 py-3 text-sm text-gray-500">
                No cities found for "{searchQuery}"
              </div>
            )}
          </div>

          {/* Auto-filled preview */}
          {selected && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600 mb-2">Auto-filled from Nominatim</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <div><span className="text-gray-500">City:</span> <span className="font-medium text-gray-900">{selected.cityLabel}</span></div>
                <div><span className="text-gray-500">Country:</span> <span className="font-medium text-gray-900">{selected.countryLabel}</span></div>
                <div><span className="text-gray-500">Latitude:</span> <span className="font-mono text-gray-900">{selected.lat}</span></div>
                <div><span className="text-gray-500">Longitude:</span> <span className="font-mono text-gray-900">{selected.lng}</span></div>
              </div>
            </div>
          )}
        </section>

        {/* Manual override fields (hidden inputs + visible read-only display) */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Location Details
            <span className="ml-2 text-xs font-normal normal-case text-gray-400">(auto-filled above, edit if needed)</span>
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Country code / slug" placeholder="de" hint="e.g. de, at, nl" error={errors.country?.message} {...register('country')} />
            <Input label="City slug" placeholder="berlin" hint="Lowercase, URL-safe" error={errors.city?.message} {...register('city')} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Country name" placeholder="Germany" error={errors.countryLabel?.message} {...register('countryLabel')} />
            <Input label="City name" placeholder="Berlin" error={errors.cityLabel?.message} {...register('cityLabel')} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Latitude" type="number" step="any" placeholder="52.52" error={errors.latitude?.message}
              {...register('latitude', { setValueAs: v => v === '' ? null : parseFloat(v) })} />
            <Input label="Longitude" type="number" step="any" placeholder="13.405" error={errors.longitude?.message}
              {...register('longitude', { setValueAs: v => v === '' ? null : parseFloat(v) })} />
          </div>
        </section>

        {/* Flag upload */}
        <section>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Flag / Icon</h2>
          <div
            {...getRootProps()}
            className={`relative border-2 border-dashed rounded-xl transition-colors cursor-pointer
              ${isDragActive ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50/50'}`}
          >
            <input {...getInputProps()} />
            {pendingFlag ? (
              <div className="flex items-center gap-4 p-4">
                <img src={pendingFlag.preview} alt="flag" className="w-14 h-14 object-cover rounded-xl border border-gray-200 shadow-sm flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">{pendingFlag.file.name}</p>
                  <p className="text-xs text-indigo-500 mt-0.5 font-medium">Will upload on submit</p>
                  <p className="text-xs text-gray-400">Drop a new image to replace</p>
                </div>
                <button type="button" onClick={clearFlag} className="text-gray-300 hover:text-red-500 transition-colors p-1">
                  <i className="ti ti-x" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
                  <i className="ti ti-photo-up text-gray-400 text-xl" />
                </div>
                <p className="text-sm font-medium text-gray-600">{isDragActive ? 'Drop image here' : 'Upload flag or icon'}</p>
                <p className="text-xs text-gray-400 mt-1">PNG, JPG, SVG, WebP · Click or drag & drop</p>
              </div>
            )}
          </div>
        </section>

        <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
          <Button type="submit" loading={createMut.isPending} disabled={createMut.isPending}>
            Create Location
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/locations')}>Cancel</Button>
        </div>
      </form>
    </div>
  )
}
